import React from 'react';

import { history } from 'umi';

import Badge from '@/components/Badge';
import TableRowButtons from '@/components/Common/TableRowButtons';
import { createEnumOptions } from '@/utils/core/data-transformation';
import { getEnumTranslationKey } from '@/utils/core/data-transformation/enumHelpers';
import { CoreEnums } from '@/utils/domain/constants';
import {
  createTableActionsColumn,
  formatChatThreadLabel,
  formatTZLocal,
  getEnumNamespace,
} from '@/utils/infrastructure';
import { createHighlightRender } from '@/utils/infrastructure/highlightColumns';
import Filters, { FILTER_FIELD_SIZES, registerModuleFilters } from '@/utils/input/filters';

import { applyProcessInstanceDetailRow } from '../utils/applyProcessInstanceDetailRow';
import { MODEL_NAME } from './constants';

const { BaseFilters } = Filters;
const { MessageStatus, TeamsMessageFormat } = CoreEnums;

const MessageStatusBadgeColor = {
  [MessageStatus.Queued]: 'info',
  [MessageStatus.Processing]: 'warning',
  [MessageStatus.Sent]: 'success',
  [MessageStatus.Error]: 'error',
  [MessageStatus.Skipped]: 'default',
  [MessageStatus.Failed]: 'error',
};

const MessageStatusFilterBadgeOptions = Object.fromEntries(
  Object.entries(MessageStatusBadgeColor).map(([value, status]) => [
    value,
    { status, type: 'process' },
  ]),
);

const CreatedDateFilter = {
  ...Filters.DateFilter,
  label: 'global.text.createdDate',
};

const StatusFilter = {
  ...BaseFilters.EnumFilter,
  name: 'status',
  label: 'global.text.status',
  enumNamespace: getEnumNamespace(CoreEnums.MessageStatus, 'Status'),
  enumSource: CoreEnums.MessageStatus,
  options: createEnumOptions(CoreEnums.MessageStatus, 'global.messageStatus'),
  badgeOptions: MessageStatusFilterBadgeOptions,
  badgeType: 'process',
};

const ConversationDestinationTypeFilter = {
  ...BaseFilters.EnumFilter,
  name: 'conversationDestinationType',
  label: 'global.teams.conversationDestinationType',
  enumNamespace: getEnumNamespace(CoreEnums.TeamsConversationDestinationType, 'Teams'),
  enumSource: CoreEnums.TeamsConversationDestinationType,
  options: createEnumOptions(
    CoreEnums.TeamsConversationDestinationType,
    'global.teamsConversationDestinationType',
    { casing: 'camel' },
  ),
};

const MessageFormatFilter = {
  ...BaseFilters.EnumFilter,
  name: 'messageFormat',
  label: 'global.teams.messageFormat',
  enumNamespace: getEnumNamespace(CoreEnums.TeamsMessageFormat, 'Teams'),
  enumSource: CoreEnums.TeamsMessageFormat,
  options: createEnumOptions(CoreEnums.TeamsMessageFormat, 'global.teamsMessageFormat', {
    casing: 'camel',
  }),
};

const ProcessInstanceFilter = {
  ...BaseFilters.RemoteFilter,
  name: 'processInstanceId',
  label: 'global.text.application',
  knownType: CoreEnums.FormFieldType.InternalEntity,
  apiRoute: 'processInstance/requests/0',
  labelInValue: true,
  filterSearch: true,
  filterSize: FILTER_FIELD_SIZES.MEDIUM,
  params: {
    select: 'id,name',
    orderBy: 'name asc',
  },
};

const AgentFilter = {
  ...BaseFilters.RemoteFilter,
  name: 'agentId',
  label: 'global.text.agent',
  apiRoute: 'ai/agent/allowed',
  filterSearch: true,
  params: {
    select: 'id,name',
    filter: 'isActive eq true',
    orderBy: 'name asc',
  },
};

const ChatThreadFilter = {
  ...BaseFilters.RemoteFilter,
  name: 'chatThreadId',
  label: 'global.email.chatThread',
  apiRoute: 'chat/thread/paged',
  uuidRouteFragments: ['agentId'],
  uuidRouteFragmentsOptional: true,
  filterSearch: true,
  params: {
    select: 'id,number,name',
    orderBy: 'number asc',
  },
  nomenclatureMapRemoteData: (data) =>
    (data || []).map((item) => ({
      ...item,
      label: formatChatThreadLabel(item) || item.name || item.label,
    })),
};

export const filtersConfig = [
  CreatedDateFilter,
  StatusFilter,
  ConversationDestinationTypeFilter,
  MessageFormatFilter,
  ProcessInstanceFilter,
  AgentFilter,
  ChatThreadFilter,
];

registerModuleFilters(MODEL_NAME, filtersConfig);

/**
 * Preserves AdaptiveCard JSON rendering, process-instance navigation and chat-thread label.
 */
export function mapTeamsMessageTraceDetailFields(rows, { item }) {
  if (!item || !rows?.length) return rows;

  const withProcessLink = applyProcessInstanceDetailRow(rows, item, history);

  return withProcessLink.map((row) => {
    if (row.name === 'status') {
      return {
        ...row,
        badgeType: 'process',
        badgeStatus: MessageStatusBadgeColor[item.status] || 'default',
      };
    }

    if (row.name === 'chatThread') {
      const label = formatChatThreadLabel(item.chatThread);
      if (!label) return row;
      return { ...row, value: label, locale: false };
    }

    if (
      row.name === 'message' &&
      item.messageFormat === TeamsMessageFormat.AdaptiveCard &&
      row.value
    ) {
      return { ...row, type: 'json', viewComponent: undefined };
    }
    return row;
  });
}

export function buildTeamsMessageTraceColumns({
  formatMessage,
  openDrawerCreateOrEdit,
  searchCriteria,
}) {
  const highlight = createHighlightRender(searchCriteria);

  return [
    {
      key: 'created',
      title: formatMessage({ id: 'global.created' }),
      dataIndex: 'created',
      sorter: true,
      render: (value) => (value ? formatTZLocal(value) : null),
    },
    {
      key: 'status',
      title: formatMessage({ id: 'global.text.status' }),
      dataIndex: 'status',
      sorter: true,
      align: 'center',
      render: (status) => {
        const color = MessageStatusBadgeColor[status] || 'default';
        const text = formatMessage({
          id:
            getEnumTranslationKey(CoreEnums.MessageStatus, status, 'global.messageStatus') ||
            'global.empty',
        });
        return <Badge status={color} type="process" text={text} />;
      },
    },
    {
      key: 'title',
      title: formatMessage({ id: 'global.text.title' }),
      dataIndex: 'title',
      sorter: true,
      textWrap: 'word-break',
      render: highlight,
    },
    {
      key: 'destinationDisplay',
      title: formatMessage({ id: 'global.text.destination' }),
      dataIndex: 'destinationDisplay',
      sorter: true,
      render: highlight,
    },
    createTableActionsColumn({
      inlineActionCount: 1,
      render: (el) => (
        <TableRowButtons
          onDetail={() => openDrawerCreateOrEdit(el.id, true)}
          formatMessage={formatMessage}
          actionOrder={{ primary: ['detail'], secondary: [] }}
        />
      ),
    }),
  ];
}
